import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import DataPill from "@components/pills/DataPill";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { ReactNode, useEffect, useRef } from "react";
import ConstantFunctions from "../../utils/constants_functions";
import { FintracCopyValue } from "./fintracCopy";
import { displayText, FINTRAC_DEFAULT_COL_DEF } from "./fintracFormat";

const constantFunctions = new ConstantFunctions();

export const formatFintracAmount = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  return constantFunctions.formatAmount(
    typeof value === "number" ? value : String(value),
  );
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  PENDING_REVIEW: { bg: "bg-[#FFF0E4]", text: "#FE961C" },
  REPORTED: { bg: "bg-[#E2F6E8]", text: "#66AD7B" },
  NOT_REPORTABLE: { bg: "bg-[#F1F3F5]", text: "#686E92" },
  ERROR: { bg: "bg-[#FFE8E8]", text: "#D61C1C" },
  COMPLETED: { bg: "bg-[#E2F6E8]", text: "#66AD7B" },
  GENERATED: { bg: "bg-[#E2F6E8]", text: "#66AD7B" },
  FAILED: { bg: "bg-[#FFE8E8]", text: "#D61C1C" },
  RUNNING: { bg: "bg-[#C7DBFF]", text: "#1D4ED8" },
};

export const fintracStatusColors = (status?: string | null) =>
  (status && STATUS_COLORS[status]) || {
    bg: "bg-[#F1F3F5]",
    text: "#686E92",
  };

export const FintracStatusPill = ({ status }: { status?: string | null }) => {
  const colors = fintracStatusColors(status);
  return (
    <span
      className={`inline-flex h-[20px] items-center justify-center gap-1 whitespace-nowrap rounded px-[6px] text-[10px] font-medium uppercase ${colors.bg}`}
      style={{ color: colors.text }}
    >
      {status || "—"}
    </span>
  );
};

export const fintracTextColumn = (
  field: string,
  headerName: string,
  width: number,
  copyMessage?: string,
): ColDef => ({
  field,
  headerName,
  width,
  minWidth: width,
  cellDataType: "text",
  cellRenderer: (params) => (
    <FintracCopyValue value={params.value} message={copyMessage} />
  ),
});

export const fintracNumberColumn = (
  field: string,
  headerName: string,
  width: number,
  copyMessage?: string,
): ColDef => ({
  field,
  headerName,
  width,
  minWidth: width,
  cellDataType: "number",
  type: "numericColumn",
  cellRenderer: (params) => (
    <FintracCopyValue value={params.value} message={copyMessage} align="end" />
  ),
});

const COUNT_TONES = {
  users: { bg: "bg-[#C7DBFF]", text: "#1D4ED8" },
  transactions: { bg: "bg-[#E2D4FF]", text: "#7C3AED" },
} as const;

export const FintracCountPill = ({
  value,
  tone,
}: {
  value: unknown;
  tone: keyof typeof COUNT_TONES;
}) => {
  const colors = COUNT_TONES[tone];
  const text = displayText(value);
  return (
    <span
      className={`inline-flex h-[20px] min-w-[28px] items-center justify-center whitespace-nowrap rounded px-[6px] text-[10px] font-semibold ${colors.bg}`}
      style={{ color: colors.text }}
    >
      {text}
    </span>
  );
};

export const fintracCountColumn = (
  field: string,
  headerName: string,
  width: number,
  tone: keyof typeof COUNT_TONES,
): ColDef => ({
  field,
  headerName,
  width,
  minWidth: width,
  cellDataType: "number",
  cellRenderer: (params) => (
    <div className="flex h-full items-center">
      <FintracCountPill value={params.value} tone={tone} />
    </div>
  ),
});

export const fintracAccountTypeColumn = (
  field: string,
  headerName: string,
  width: number,
): ColDef => ({
  field,
  headerName,
  width,
  minWidth: width,
  cellRenderer: (params) => {
    const accountType =
      typeof params.value === "string" ? params.value.trim() : "";
    return (
      <div className="flex h-full items-center">
        <DataPill value={accountType || null} />
      </div>
    );
  },
});

export const fintracAmountColumn = (
  field: string,
  headerName: string,
  width: number,
): ColDef => ({
  field,
  headerName,
  width,
  minWidth: width,
  cellDataType: "text",
  type: "numericColumn",
  cellRenderer: (params) => {
    const text = formatFintracAmount(params.value);
    return (
      <span
        className="block min-w-0 truncate"
        title={text === "—" ? undefined : text}
      >
        {text}
      </span>
    );
  },
});

export const FintracHeader = ({
  crumbs,
  detail,
}: {
  crumbs: { label: string; onClick?: () => void }[];
  detail?: ReactNode;
}) => (
  <div className="sticky top-0 z-10 flex h-[70px] shrink-0 items-center border-b border-border-subtle bg-bg-page-body px-[20px]">
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1 text-[14px] font-semibold text-text-base">
        {crumbs.map((crumb, index) => (
          <span
            key={`${crumb.label}-${index}`}
            className="flex items-center gap-1"
          >
            {index > 0 && (
              <span className="font-normal text-text-subtle">/</span>
            )}
            {crumb.onClick ? (
              <button
                type="button"
                onClick={crumb.onClick}
                className="text-text-base hover:underline"
              >
                {crumb.label}
              </button>
            ) : (
              <span>{crumb.label}</span>
            )}
          </span>
        ))}
      </div>
      {detail && (
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-[11px] text-text-subtle">
          {detail}
        </div>
      )}
    </div>
  </div>
);

const isNearListEnd = (api: {
  getDisplayedRowCount: () => number;
  getLastDisplayedRowIndex: () => number;
}) => {
  const displayedRowCount = api.getDisplayedRowCount();
  const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();
  return (
    displayedRowCount > 0 && lastDisplayedRowIndex >= displayedRowCount - 5
  );
};

export const FintracTable = <T extends { id: number }>({
  columnDefs,
  rows,
  loading,
  loadingMore = false,
  error,
  totalRecords = 0,
  hasMore = false,
  onLoadMore,
  onRowClicked,
  selectedRowId,
  rowHeight = 48,
}: {
  columnDefs: ColDef[];
  rows: T[];
  loading: boolean;
  loadingMore?: boolean;
  error: string;
  totalRecords?: number;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onRowClicked?: (event: RowClickedEvent<T>) => void;
  selectedRowId?: number | null;
  rowHeight?: number;
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const showInitialError = Boolean(error) && rows.length === 0 && !loading;

  useEffect(() => {
    gridRef.current?.api?.redrawRows();
  }, [selectedRowId]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || loading || loadingMore || !hasMore) return;
    if (isNearListEnd(api)) onLoadMore?.();
  }, [hasMore, loading, loadingMore, onLoadMore, rows]);

  let body: ReactNode;
  if (loading) {
    body = (
      <AgGridSkeleton
        columnDefs={columnDefs}
        defaultColDef={FINTRAC_DEFAULT_COL_DEF}
        className="fintrac-grid"
        style={{ height: "100%", width: "100%" }}
      />
    );
  } else if (showInitialError) {
    body = (
      <div className="flex h-full items-center justify-center px-6 text-center text-[13px] text-text-status-red">
        {error}
      </div>
    );
  } else {
    body = (
      <AgGridReactTable
        ref={gridRef}
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={FINTRAC_DEFAULT_COL_DEF}
        onRowClicked={onRowClicked}
        onBodyScroll={(params) => {
          if (isNearListEnd(params.api)) onLoadMore?.();
        }}
        rowHeight={rowHeight}
        getRowId={(params) => String(params.data?.id)}
        getRowClass={(params) => {
          const classes: string[] = [];
          if (onRowClicked) classes.push("cursor-pointer");
          if (selectedRowId != null && params.data?.id === selectedRowId) {
            classes.push("ag-row-selected-custom");
          }
          return classes.join(" ");
        }}
        overlayNoRowsTemplate="<span>No records</span>"
        className="ag-theme-alpine fintrac-grid overflow-hidden"
        style={{ height: "100%", width: "100%" }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1">{body}</div>
      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border-subtle pt-3">
        <span className="text-[11px] text-text-subtle">
          {totalRecords} records
        </span>
        {loadingMore && (
          <span className="flex items-center gap-2 text-[11px] text-text-subtle">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-border-subtle border-t-border-button-primary" />
            Loading more
          </span>
        )}
        {!loadingMore && error && rows.length > 0 && (
          <span className="text-[11px] text-text-status-red">{error}</span>
        )}
      </div>
    </div>
  );
};
